import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { connectionsAPI, Connection, gaAPI, GAProperty } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import WhmcsConnectModal from '../components/WhmcsConnectModal';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [mcpUrl, setMcpUrl] = useState<string>('');
  const [connectingServices, setConnectingServices] = useState<Set<string>>(new Set());
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [gaPropertiesExpanded, setGaPropertiesExpanded] = useState<Record<string, boolean>>({});
  const [gaProperties, setGaProperties] = useState<Record<string, GAProperty[]>>({});
  const [gaPropertiesLoading, setGaPropertiesLoading] = useState<Record<string, boolean>>({});
  const [whmcsModalOpen, setWhmcsModalOpen] = useState(false);
  const [whmcsConnecting, setWhmcsConnecting] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const fetchConnections = useCallback(async () => {
    try {
      const response = await connectionsAPI.listConnections();
      const servicesList = response?.services || response?.connections || [];
      
      const formattedConnections: Connection[] = servicesList.map((conn: any) => ({
        connector: conn.service || conn.connector,
        displayName: conn.display_name || getDisplayName(conn.service || conn.connector),
        connected: conn.connected || false,
        connectedAt: conn.connection_date || conn.connectedAt,
        status: conn.connected ? 'connected' : 'disconnected',
        lastSync: conn.lastSync,
        metadata: conn.metadata,
        is_enabled: conn.is_enabled,
        is_development: conn.is_development,
      }));

      setConnections(formattedConnections);
    } catch (err: any) {
      console.error('Failed to fetch connections:', err);
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
    fetchMcpUrl();
    
    const interval = setInterval(fetchConnections, 30000);
    
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'oauth-success') {
        const serviceName = getDisplayName(event.data.service);
        setConnectingServices(prev => {
          const newSet = new Set(prev);
          newSet.delete(event.data.service);
          return newSet;
        });
        toast.success(`${serviceName} connected successfully`);
        fetchConnections();
      } else if (event.data?.type === 'oauth-error') {
        setConnectingServices(prev => {
          const newSet = new Set(prev);
          newSet.delete(event.data.service);
          return newSet;
        });
        toast.error(`Failed to connect ${getDisplayName(event.data.service)}`);
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('message', handleMessage);
    };
  }, [fetchConnections]);

  const fetchMcpUrl = async () => {
    try {
      const response = await connectionsAPI.getMcpUrl();
      if (response.success && response.mcpUrl) {
        setMcpUrl(response.mcpUrl);
      }
    } catch (err: any) {
      console.error('Failed to fetch MCP URL:', err);
      setMcpUrl('https://mcp.vpsmalaysia.com.my/mcp/[token]');
    }
  };

  const getDisplayName = (connector: string): string => {
    const names: Record<string, string> = {
      'google-analytics': 'Google Analytics',
      'google-search-console': 'Google Search Console',
      'facebook-ads': 'Facebook Ads',
      'salesforce': 'Salesforce',
      'whmcs': 'WHMCS Analytics',
    };
    return names[connector] || connector;
  };

  const getServiceIcon = (connector: string): string => {
    const icons: Record<string, string> = {
      'google-analytics': '📊',
      'google-search-console': '🔍',
      'facebook-ads': '📱',
      'salesforce': '☁️',
      'whmcs': '🎯',
    };
    return icons[connector] || '🔌';
  };

  const handleConnect = async (connector: string, forceReauth: boolean = false) => {
    try {
      const connection = connections.find(c => c.connector === connector);
      if (connection?.metadata?.auth_type === 'module_token') {
        setWhmcsModalOpen(true);
        return;
      }
      
      setConnectingServices(prev => new Set(prev).add(connector));
      
      const response = await connectionsAPI.initiateOAuth(connector, forceReauth);
      
      if (response.authUrl || response.auth_url) {
        const authWindow = window.open(
          response.authUrl || response.auth_url,
          'oauth',
          'width=600,height=700'
        );
        
        if (!authWindow) {
          setConnectingServices(prev => {
            const newSet = new Set(prev);
            newSet.delete(connector);
            return newSet;
          });
          toast.error('Popup was blocked. Please allow popups and try again.');
          return;
        }
        
        window.setTimeout(() => {
          setConnectingServices(prev => {
            const newSet = new Set(prev);
            newSet.delete(connector);
            return newSet;
          });
        }, 120000);
      } else if (response.status === 'connected') {
        setConnectingServices(prev => {
          const newSet = new Set(prev);
          newSet.delete(connector);
          return newSet;
        });
        toast.success(`${getDisplayName(connector)} is already connected`);
        await fetchConnections();
      }
    } catch (err: any) {
      console.error('Failed to initiate OAuth:', err);
      setConnectingServices(prev => {
        const newSet = new Set(prev);
        newSet.delete(connector);
        return newSet;
      });
      toast.error(`Failed to connect ${getDisplayName(connector)}`);
    }
  };

  const handleDisconnect = async (connector: string) => {
    try {
      if (connector === 'whmcs') {
        await connectionsAPI.disconnectWhmcs();
      } else {
        await connectionsAPI.disconnectService(connector);
      }
      
      toast.success(`${getDisplayName(connector)} disconnected`);
      await fetchConnections();
    } catch (err: any) {
      console.error('Failed to disconnect:', err);
      toast.error(`Failed to disconnect ${getDisplayName(connector)}`);
    }
  };

  const handleWhmcsConnect = async (moduleUrl: string, moduleToken: string) => {
    try {
      setWhmcsConnecting(true);
      await connectionsAPI.connectWhmcs(moduleUrl, moduleToken);
      toast.success('WHMCS connected successfully');
      setWhmcsModalOpen(false);
      await fetchConnections();
    } catch (err: any) {
      console.error('Failed to connect WHMCS:', err);
      throw new Error(err.message || 'Failed to connect WHMCS');
    } finally {
      setWhmcsConnecting(false);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(mcpUrl);
    toast.success('URL copied to clipboard');
  };

  const handleRegenerateUrl = async () => {
    setRegenerating(true);
    try {
      const response = await connectionsAPI.regenerateMcpUrl();
      if (response.success) {
        setMcpUrl(response.mcpUrl);
        toast.success('New URL generated');
        setRegenerateDialogOpen(false);
      } else {
        toast.error('Failed to regenerate URL');
      }
    } catch (err: any) {
      console.error('Failed to regenerate MCP URL:', err);
      toast.error('Failed to regenerate URL');
    } finally {
      setRegenerating(false);
    }
  };

  const handleToggleGAProperties = async (connector: string) => {
    const isExpanded = gaPropertiesExpanded[connector];
    
    if (!isExpanded && !gaProperties[connector]) {
      setGaPropertiesLoading(prev => ({ ...prev, [connector]: true }));
      try {
        const response = await gaAPI.getUserProperties();
        if (response.success) {
          setGaProperties(prev => ({ ...prev, [connector]: response.properties }));
        } else {
          toast.error('Failed to load properties');
        }
      } catch (err: any) {
        console.error('Failed to load GA properties:', err);
        toast.error('Failed to load properties');
      } finally {
        setGaPropertiesLoading(prev => ({ ...prev, [connector]: false }));
      }
    }
    
    setGaPropertiesExpanded(prev => ({ ...prev, [connector]: !isExpanded }));
  };

  const handlePropertyToggle = async (connector: string, property: GAProperty) => {
    try {
      const response = await gaAPI.updatePropertySettings(property.propertyId, {
        propertyName: property.propertyName,
        accountId: property.accountId,
        accountName: property.accountName,
        isEnabled: !property.isEnabled,
      });
      
      if (response.success) {
        setGaProperties(prev => ({
          ...prev,
          [connector]: prev[connector].map(p => 
            p.propertyId === property.propertyId 
              ? { ...p, isEnabled: !p.isEnabled }
              : p
          )
        }));
        
        toast.success(`Property ${!property.isEnabled ? 'enabled' : 'disabled'}`);
      }
    } catch (err: any) {
      console.error('Failed to update property:', err);
      toast.error('Failed to update property');
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-12 py-16">
        <div className="text-center text-gray-500">Loading...</div>
      </div>
    );
  }

  const connectedCount = connections.filter(c => c.connected).length;

  return (
    <div className="max-w-6xl mx-auto px-12 py-16">
      {/* Header */}
      <div className="mb-16">
        <h1 className="text-3xl font-light mb-2 tracking-tight">
          Welcome back, {user?.fullName || user?.email?.split('@')[0]}
        </h1>
        <p className="text-sm text-gray-600">
          Manage your MCP connections and enhance your AI experience
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-6 mb-16">
        <div className="bg-white border border-gray-300 rounded-lg p-6">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">
            Total Connections
          </div>
          <div className="text-4xl font-light tracking-tight">
            {connectedCount}
          </div>
        </div>
        <div className="bg-white border border-gray-300 rounded-lg p-6">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">
            Available Services
          </div>
          <div className="text-4xl font-light tracking-tight">
            {connections.length}
          </div>
        </div>
        <div className="bg-white border border-gray-300 rounded-lg p-6">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">
            MCP Status
          </div>
          <div className="text-4xl font-light tracking-tight text-accent-500 flex items-center">
            Active
            <span className="inline-block w-2 h-2 bg-accent-500 rounded-full ml-2"></span>
          </div>
        </div>
      </div>

      {/* MCP URL Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-8 mb-16">
        <h2 className="text-sm font-medium mb-2">Your MCP Connection URL</h2>
        <p className="text-sm text-gray-600 mb-6">
          Copy this URL and add it to Claude Web or any MCP-compatible AI assistant
        </p>
        <div className="flex items-center gap-4">
          <input
            type="text"
            className="flex-1 font-mono text-sm px-4 py-3 bg-gray-150 rounded-md text-gray-700"
            value={showUrl ? mcpUrl : '••••••••••••••••••••••••••••••••'}
            readOnly
          />
          <div className="flex gap-2">
            <button
              onClick={() => setShowUrl(!showUrl)}
              className="btn-icon"
              title={showUrl ? 'Hide URL' : 'Show URL'}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                {showUrl ? (
                  <>
                    <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/>
                    <circle cx="8" cy="8" r="2"/>
                  </>
                ) : (
                  <>
                    <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/>
                    <line x1="3" y1="3" x2="13" y2="13"/>
                  </>
                )}
              </svg>
            </button>
            <button
              onClick={handleCopyUrl}
              className="btn-icon"
              title="Copy URL"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="5.5" y="5.5" width="7" height="7" rx="1"/>
                <path d="M3.5 10.5v-7a1 1 0 011-1h7"/>
              </svg>
            </button>
            <button
              onClick={() => setRegenerateDialogOpen(true)}
              className="btn-icon"
              title="Regenerate URL"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2 8a6 6 0 0112 0 6 6 0 01-12 0"/>
                <path d="M2 4v4h4"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Connections */}
      <h2 className="text-lg font-normal mb-8 tracking-tight">Your Connections</h2>
      <div className="grid grid-cols-2 gap-6">
        {connections
          .filter(connection => connection.is_enabled !== false)
          .map((connection) => {
            const isConnecting = connectingServices.has(connection.connector);
            
            return (
              <div
                key={connection.connector}
                className="bg-white border border-gray-300 rounded-lg p-6 transition-colors duration-200 hover:border-gray-400"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center text-lg">
                      {getServiceIcon(connection.connector)}
                    </div>
                    <div>
                      <h3 className="text-sm font-medium">
                        {connection.displayName}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {connection.connected && connection.connectedAt ? (
                          `Connected ${new Date(connection.connectedAt).toLocaleDateString()}`
                        ) : (
                          `Connect to use ${connection.displayName} in your AI assistant`
                        )}
                      </p>
                    </div>
                  </div>
                  
                  {connection.connected ? (
                    <button
                      onClick={() => handleDisconnect(connection.connector)}
                      className="px-5 py-2 text-sm text-gray-600 hover:text-black transition-colors duration-200"
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={() => handleConnect(connection.connector)}
                      disabled={isConnecting}
                      className={cn(
                        "btn-secondary",
                        isConnecting && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {isConnecting ? 'Connecting...' : 'Connect'}
                    </button>
                  )}
                </div>

                {/* GA Properties - simplified */}
                {connection.connector === 'google-analytics' && connection.connected && (
                  <div className="mt-4 pt-4 border-t border-gray-300">
                    <button
                      onClick={() => handleToggleGAProperties(connection.connector)}
                      className="text-xs text-gray-600 hover:text-black transition-colors duration-200"
                    >
                      {gaPropertiesExpanded[connection.connector] ? 'Hide' : 'Show'} Properties
                    </button>
                    
                    {gaPropertiesExpanded[connection.connector] && (
                      <div className="mt-3 space-y-2">
                        {gaPropertiesLoading[connection.connector] ? (
                          <div className="text-xs text-gray-500">Loading...</div>
                        ) : gaProperties[connection.connector]?.length > 0 ? (
                          gaProperties[connection.connector].map((property) => (
                            <div
                              key={property.propertyId}
                              className="flex items-center justify-between py-1"
                            >
                              <div>
                                <div className="text-xs font-medium">{property.propertyName}</div>
                                <div className="text-xs text-gray-500">{property.accountName}</div>
                              </div>
                              <input
                                type="checkbox"
                                checked={property.isEnabled}
                                onChange={() => handlePropertyToggle(connection.connector, property)}
                                className="rounded border-gray-300"
                              />
                            </div>
                          ))
                        ) : (
                          <div className="text-xs text-gray-500">No properties found</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* Regenerate Dialog */}
      {regenerateDialogOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40"
            onClick={() => setRegenerateDialogOpen(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white border border-gray-300 rounded-lg p-6 z-50 w-96">
            <h3 className="text-base font-medium mb-2">Regenerate MCP URL?</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will generate a new URL and invalidate your current one. 
              You'll need to update the URL in Claude Web.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setRegenerateDialogOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-black transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerateUrl}
                disabled={regenerating}
                className="btn-primary"
              >
                {regenerating ? 'Regenerating...' : 'Regenerate'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* WHMCS Modal */}
      <WhmcsConnectModal
        open={whmcsModalOpen}
        onClose={() => setWhmcsModalOpen(false)}
        onConnect={handleWhmcsConnect}
        isConnecting={whmcsConnecting}
      />
    </div>
  );
};

export default Dashboard;